import React, { useState } from 'react';
import { Card, CardHeader, Typography, Box, IconButton, Stack, ImageList, ImageListItem } from '@mui/material';
import { IThougth, UserSummary } from '../../types/global/helper';
import { IActivityFeedItem } from '../../redux/activity/types';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import EyeIcon from '../CustomSVGIcons/EyeIcon';
import CustomCommentIcon from '../CustomSVGIcons/CustomCommentIcon';
import LoveIcon from '../CustomSVGIcons/LoveIcon';
import { convertMinuteSecondsDays, formatNumberWithK } from '../../utils/AppUtils';
import { EsFollowButton } from '../EsButtons/EsFollowButton';
import { BackgroundColorCard } from './BackGroundColorCard';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import 'react-photo-view/dist/react-photo-view.css';
import { useDispatch, useSelector } from 'react-redux';
import { likeActivity, deleteActivityLike } from '../../redux/activity/actions';
import { activityLikeActionInProgressSelector } from '../../selectors/selectors';
import { useAuth } from '../../hook/useAuth';
import { toggleAuthModal } from '../../redux/authentication/action';

interface ThoughtCardProps {
    activity?: IActivityFeedItem;
    onViewClick?: VoidFunction;
    onCommentClick?: VoidFunction;
    onLikeClick?: VoidFunction;
    previewMode?: boolean;
}

// Custom PhotoGrid component for ThoughtCard with proper space filling
const ThoughtPhotoGrid: React.FC<{ photos: string[]; columns: number }> = ({ photos, columns }) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const maxHeight = 400;

    // Show only first 5 images, then count overlay
    const maxImagesToShow = 5;
    const imagesToShow = photos.slice(0, maxImagesToShow);
    const remainingCount = photos.length - maxImagesToShow;

    // Calculate optimal height based on number of images and columns
    const getOptimalHeight = () => {
        const displayCount = Math.min(photos.length, maxImagesToShow + (remainingCount > 0 ? 1 : 0));
        const rows = Math.ceil(displayCount / columns);
        return Math.min(maxHeight / rows, maxHeight);
    };

    const imageHeight = getOptimalHeight();

    return (
        <PhotoProvider>
            <ImageList
                sx={{
                    width: '100%',
                    margin: 0,
                    padding: 0,
                    overflow: 'hidden',
                }}
                cols={columns}
                rowHeight={imageHeight}
                gap={2}>
                {imagesToShow.map((item, index) => (
                    <ImageListItem
                        key={index}
                        onMouseEnter={() => setHoverIndex(index)}
                        onMouseLeave={() => setHoverIndex(null)}
                        sx={{
                            width: '100%',
                            height: imageHeight,
                            position: 'relative',
                            cursor: 'pointer',
                            padding: 0,
                        }}>
                        <PhotoView src={item} triggers={['onClick', 'onDoubleClick']}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: '4px',
                                }}>
                                <img
                                    src={item}
                                    alt={`thought-media-${index}`}
                                    loading="lazy"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                                {hoverIndex === index && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '4px',
                                            transition: 'opacity 0.3s ease-in-out',
                                        }}>
                                        <ZoomInIcon sx={{ color: 'white', fontSize: 30 }} />
                                    </Box>
                                )}
                            </Box>
                        </PhotoView>
                    </ImageListItem>
                ))}

                {/* Count overlay for remaining images */}
                {remainingCount > 0 && (
                    <ImageListItem
                        sx={{
                            width: '100%',
                            height: imageHeight,
                            position: 'relative',
                            cursor: 'pointer',
                            padding: 0,
                        }}>
                        <PhotoView src={photos[5]} triggers={['onClick', 'onDoubleClick']}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: '4px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                }}>
                                +{remainingCount}
                            </Box>
                        </PhotoView>
                    </ImageListItem>
                )}
            </ImageList>
        </PhotoProvider>
    );
};

const ThoughtCard: React.FC<ThoughtCardProps> = ({
    activity,
    onCommentClick,
    onLikeClick,
    onViewClick,
    previewMode,
}) => {
    const dispatch = useDispatch();
    const { created_by, createdAt, thougths_activity } = activity || {};
    const { images, thougth, is_background_color, background_color } = (thougths_activity as IThougth) || {};
    const { themeStyle } = useThemeContext();
    const likingInProgress = useSelector(activityLikeActionInProgressSelector);
    const { isSignedIn } = useAuth();

    const handleLikeClick = () => {
        if (!isSignedIn) {
            dispatch(toggleAuthModal(true));
            return;
        }
        // Don't proceed if liking is already in progress
        if (likingInProgress) {
            return;
        }

        const thoughtActivity = thougths_activity as IThougth;

        if (thoughtActivity?.is_user_liked && thoughtActivity?.user_like && thoughtActivity.user_like.length > 0) {
            // User wants to unlike - use deleteActivityLike
            dispatch(
                deleteActivityLike({
                    likeId: thoughtActivity.user_like[0]._id,
                    activityId: thoughtActivity._id,
                    like_category: 'thougth',
                    like_origin: 'details',
                    additionalPayload: thoughtActivity,
                }),
            );
        } else {
            // User wants to like - use likeActivity
            dispatch(
                likeActivity({
                    activityId: thoughtActivity._id,
                    like_category: 'thougth',
                    like_type: 'like',
                    like_origin: 'details',
                }),
            );
        }
    };

    const getOptimalColumns = (imageCount: number) => {
        if (imageCount === 1) return 1;
        if (imageCount === 2) return 2;
        if (imageCount === 3) return 3;
        if (imageCount === 4) return 2; // 2x2 grid
        if (imageCount === 5 || imageCount === 6) return 3; // 3x2 grid
        return 4; // Default for 7+ images
    };

    const renderMedia = () => {
        const totalImages = images ? images?.length : 0;

        if (totalImages === 0) return null;

        const columns = getOptimalColumns(totalImages);

        return (
            <Box sx={{ maxHeight: '400px', overflow: 'hidden' }}>
                <ThoughtPhotoGrid photos={images} columns={columns} />
            </Box>
        );
    };

    return (
        <Card
            variant="outlined"
            sx={{
                marginBottom: 2,
                maxHeight: '700px',
                backgroundColor: theme[themeStyle].secondaryBg,
                ...(previewMode && { borderColor: theme[themeStyle].darkGray60 }),
            }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mr={2}>
                {created_by && (
                    <CardHeader
                        avatar={<ThspianAvatar user={created_by} withMonogram enableDefaultNavigation />}
                        title={`${created_by.userName}`}
                        subheader={convertMinuteSecondsDays(createdAt) + ' ago'}
                        titleTypographyProps={{
                            textAlign: 'left',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].tag_white,
                            fontSize: 14,
                        }}
                        subheaderTypographyProps={{
                            textAlign: 'left',
                            color: theme[themeStyle].tag_white,
                            fontSize: 8,
                        }}
                    />
                )}
                {created_by && (
                    <EsFollowButton
                        user={created_by as unknown as UserSummary}
                        intrinsic
                        isFollowing={created_by.user_following}
                        location="feed"
                    />
                )}
            </Stack>
            {thougth && (
                <Box>
                    {is_background_color && background_color ? (
                        <BackgroundColorCard backgroundColor={background_color} text={thougth} />
                    ) : (
                        <Typography
                            sx={{
                                marginBottom: 2,
                                textAlign: 'left',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].tag_white,
                                px: 3,
                                fontSize: 14,
                            }}>
                            {thougth}
                        </Typography>
                    )}
                    {renderMedia()}
                </Box>
            )}
            <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                <Box display="flex" alignItems="center">
                    <IconButton
                        sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: '50%' }}
                        onClick={onViewClick}>
                        <EyeIcon stroke={theme[themeStyle].tag_white} height={'20px'} width={'20px'} />
                    </IconButton>
                    <Typography
                        style={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].tag_white,
                            fontSize: 12,
                        }}
                        marginLeft={1}>
                        {formatNumberWithK(thougths_activity?.view_count!)}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <IconButton
                        sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: '50%' }}
                        onClick={onCommentClick}>
                        <CustomCommentIcon stroke={theme[themeStyle].tag_white} height={'20px'} width={'20px'} />
                    </IconButton>
                    <Typography
                        style={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].tag_white,
                            fontSize: 12,
                        }}
                        marginLeft={1}>
                        {formatNumberWithK(thougths_activity?.total_comments!)}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <IconButton
                        sx={{
                            bgcolor: theme[themeStyle].deep_grey,
                            borderRadius: '50%',
                            '&:disabled': {
                                bgcolor: theme[themeStyle].deep_grey,
                                opacity: 0.7,
                            },
                            cursor: 'pointer',
                        }}
                        onClick={handleLikeClick}
                        disabled={likingInProgress}>
                        <LoveIcon
                            fill={
                                (thougths_activity as IThougth)?.is_user_liked
                                    ? theme[themeStyle].secondary_red
                                    : theme[themeStyle].tag_white
                            }
                            filled={(thougths_activity as IThougth)?.is_user_liked}
                            height={'20px'}
                            width={'20px'}
                        />
                    </IconButton>
                    <Typography
                        style={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: (thougths_activity as IThougth)?.is_user_liked
                                ? theme[themeStyle].switchGreen
                                : theme[themeStyle].tag_white,
                            fontSize: 12,
                        }}
                        marginLeft={1}>
                        {formatNumberWithK(thougths_activity?.total_likes!)}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center">
                    <IconButton sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: '50%' }}>
                        <SignalCellularAltIcon
                            sx={{
                                fontSize: '16px',
                                color: theme[themeStyle].tag_white,
                            }}
                        />
                    </IconButton>
                    <Typography
                        style={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].tag_white,
                            fontSize: 12,
                        }}
                        marginLeft={1}>
                        {formatNumberWithK(thougths_activity?.trending_marker!)}
                    </Typography>
                </Box>
            </Box>
        </Card>
    );
};

export { ThoughtCard };
